import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Loading from "@/components/Loading/Loading";
import api from "@/utils/axios";
import cls from "./ClubEvents.module.scss";

const ALLOWED_STATES = new Set([
  "activeClubMember",
  "activeClubHost",
  "activeClubPartner",
]);

const safeStr = (v) => (v == null ? "" : String(v));
const hasText = (v) => safeStr(v).trim().length > 0;

const EVENT_SECTIONS = [
  {
    icon: "🪩",
    title: "In-Person Events",
    description:
      "Meet other members face-to-face and enjoy real-life experiences together. These events may include social gatherings, themed parties, casual meetups, or private member events where people can relax, connect, and have fun in a safe environment.",
  },
  {
    icon: "💻",
    title: "Online Events",
    description:
      "Members will also be able to join live online events directly through the platform. These may include livestream shows, hosted sessions, entertainment events, or exclusive digital experiences that you can watch from anywhere.",
  },
  {
    icon: "💬",
    title: "Online Discussions",
    description:
      "For members who enjoy conversations and sharing ideas, we will host interactive online discussion sessions. Members can join the discussion, share thoughts, ask questions, and connect with others in a respectful and open-minded environment.",
  },
  {
    icon: "🎟",
    title: "Free & Paid Events",
    description:
      "Some events will be free for members, while others may require a ticket or event fee depending on the type of experience being offered.",
  },
];

const ClubEvents = () => {
  const { t } = useTranslation();
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const { data } = await api.get(`/v1/lounge/clubs/${clubId}`);
        if (ignore) return;

        const memberState = safeStr(data?.memberState);
        if (!ALLOWED_STATES.has(memberState)) {
          navigate(`/lounge/clubs/single/${clubId}`, { replace: true });
          return;
        }

        setPayload(data || null);
      } catch (err) {
        if (ignore) return;
        setError(
          err?.response?.data?.message ||
            t("clubEvents.states.error", "Something went wrong."),
        );
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [clubId, navigate, t]);

  const club = payload?.club || null;

  const clubName =
    safeStr(club?.clubName) || t("clubEvents.header.clubName", "Club");

  const clubNo = club?.clubNo != null ? String(club.clubNo) : "";
  const streetName = safeStr(club?.streetName);
  const cityName = safeStr(club?.cityName);

  const clubAddressParts = [
    hasText(clubNo) ? `${t("clubEvents.header.clubNo", "Club")} ${clubNo}` : "",
    streetName,
    cityName,
  ].filter((x) => hasText(x));

  const clubAddress = clubAddressParts.length
    ? clubAddressParts.join(" · ")
    : t("clubEvents.header.addressFallback", "Address not available");

  const clubBase = `/lounge/clubs/single/${clubId}`;
  const clubTabs = [
    {
      key: "feed",
      label: t("clubHome.tabs.feed", "Feed"),
      to: `${clubBase}/home`,
    },
    {
      key: "members",
      label: t("clubHome.tabs.members", "Members"),
      to: `${clubBase}/members`,
    },
    {
      key: "events",
      label: t("clubHome.tabs.events", "Events"),
      to: `${clubBase}/events`,
    },
    {
      key: "shop",
      label: t("clubHome.tabs.shop", "Shop"),
      to: `/lounge/marketplace/${clubId}`,
    },
  ];

  const isActiveTab = (to) => {
    const p = safeStr(location.pathname);
    if (p === to) return true;
    if (
      to.endsWith("/home") &&
      (p === clubBase || p.startsWith(`${clubBase}/home`))
    ) {
      return true;
    }
    return p.startsWith(to);
  };

  const topTabs = (
    <div className={cls.tabs}>
      {clubTabs.map((tab) => (
        <Link
          key={tab.key}
          to={tab.to}
          className={isActiveTab(tab.to) ? cls.tabActive : cls.tab}
        >
          {tab.label}
        </Link>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className={cls.page}>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cls.page}>
        <div className={cls.centerCol}>
          <div className={cls.stateRowError}>
            <span className={cls.stateDot} />
            <span className={cls.stateText}>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.centerCol}>
        <header className={cls.header}>
          <div className={cls.headerInfo}>
            <p className={cls.clubLabel}>Club Events – Coming Soon</p>
            <h1 className={cls.displayName}>{clubName}</h1>
            <p className={cls.address}>{clubAddress}</p>
            <p className={cls.helperText}>
              Something exciting is on the way for our members.
            </p>
          </div>
          <div className={cls.headerLinks}>
            <Link
              className={cls.btnGhost}
              to={
                isPublic
                  ? "/lounge"
                  : isFriends
                    ? "/lounge/friends-feed"
                    : isClubs
                      ? "/lounge/clubs-feed"
                      : isChat
                        ? "/lounge/chat"
                        : isSearch
                          ? "/lounge/search"
                          : "/lounge"
              }
            >
              {t("clubEvents.header.back", "Back to Lounge")}
            </Link>
            <Link className={cls.btnPrimary} to={`${clubBase}/home`}>
              {t("clubEvents.header.clubHome", "Club Home")}
            </Link>
          </div>
        </header>

        {topTabs}

        <section className={cls.heroCard}>
          <p className={cls.heroKicker}>At Noty Universe</p>
          <h2>
            We believe the best connections happen through shared experiences.
          </h2>
          <p>
            Our team is currently organizing a variety of exclusive events
            designed to bring members together in fun, relaxed, and meaningful
            ways.
          </p>
          <p>
            Soon, members will be able to explore and join different types of
            events inside the platform.
          </p>
        </section>

        <section className={cls.grid}>
          {EVENT_SECTIONS.map((item) => (
            <article key={item.title} className={cls.infoCard}>
              <div className={cls.iconBubble}>{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </article>
          ))}
        </section>
        <section className={cls.footNote}>
          <p>
            All event details, schedules, and booking options will be available
            inside the platform once events are launched.
          </p>
          <p className={cls.highlight}>
            ✨ Stay tuned — the first events will be announced soon!
          </p>
        </section>
      </div>
    </div>
  );
};

export default ClubEvents;
